package com.shadowings.gero.presentation.component.avatars

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.BG_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.BODY_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EAR_LEFT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EAR_RIGHT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EYES_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.HEAD_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIA_LEFT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIA_RIGHT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIE_TYPE_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.WHISKERS_KEY
import com.shadowings.gero.presentation.screen.characters.backgroundOptions
import com.shadowings.gero.presentation.screen.characters.eyesOptions
import com.shadowings.gero.presentation.screen.characters.hairOptions
import com.shadowings.gero.presentation.screen.characters.hatBarOptions
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.neutralWhite
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.nilo_layer_0_body
import gero.composeapp.generated.resources.nilo_layer_1_ear_back_left
import gero.composeapp.generated.resources.nilo_layer_1_ear_back_right
import gero.composeapp.generated.resources.nilo_layer_2_ear_mid
import gero.composeapp.generated.resources.nilo_layer_3_ear_top
import gero.composeapp.generated.resources.nilo_layer_4_head
import gero.composeapp.generated.resources.nilo_layer_5_macchia_left
import gero.composeapp.generated.resources.nilo_layer_5_macchia_left_tigrato
import gero.composeapp.generated.resources.nilo_layer_5_macchia_right
import gero.composeapp.generated.resources.nilo_layer_5_macchia_right_tigrato
import gero.composeapp.generated.resources.nilo_layer_5_macchia_tuxedo
import gero.composeapp.generated.resources.nilo_layer_6_idle_baffi
import gero.composeapp.generated.resources.nilo_layer_6_idle_bocca_back
import gero.composeapp.generated.resources.nilo_layer_6_idle_bocca_blockers
import gero.composeapp.generated.resources.nilo_layer_6_idle_bocca_over
import gero.composeapp.generated.resources.nilo_layer_6_idle_guance
import gero.composeapp.generated.resources.nilo_layer_6_idle_naso
import gero.composeapp.generated.resources.nilo_layer_6_idle_occhi_back
import gero.composeapp.generated.resources.nilo_layer_6_idle_occhi_mid
import gero.composeapp.generated.resources.nilo_layer_6_idle_occhi_top
import gero.composeapp.generated.resources.nilo_layer_6_sad_bocca_back
import gero.composeapp.generated.resources.nilo_layer_6_sad_bocca_over
import gero.composeapp.generated.resources.nilo_layer_6_sleeping_bocca
import gero.composeapp.generated.resources.nilo_layer_6_sleeping_occhi
import gero.composeapp.generated.resources.nilo_layer_6_surprised_bocca_back
import gero.composeapp.generated.resources.nilo_layer_6_surprised_bocca_over
import org.jetbrains.compose.resources.DrawableResource


@Preview
@Composable
fun NiloAvatarPreview() {
    AppTheme {
        FlowRow {
            Faces.entries.forEach { face ->
                Column {
                    NiloAvatar(
                        face = face,
                        withBackground = true
                    )
                }
            }
        }
    }
}

@Composable
fun NiloAvatar(
    userData: UserData = UserData(),
    face: Faces = Faces.Idle,
    modifier: Modifier = Modifier.size(256.dp),
    withBackground: Boolean = true
) {

    val bgColorIndex =
        (userData.niloColors[BG_KEY] ?: 0).coerceIn(0, backgroundOptions.size - 1)
    val bodyColorIndex = (userData.niloColors[BODY_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val earLeftColorIndex =
        (userData.niloColors[EAR_LEFT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val earRightColorIndex =
        (userData.niloColors[EAR_RIGHT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val headColorIndex = (userData.niloColors[HEAD_KEY] ?: 0).coerceIn(0, hairOptions.size - 1)
    val macchiaRightColorIndex =
        (userData.niloColors[MACCHIA_RIGHT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val macchiaLeftColorIndex =
        (userData.niloColors[MACCHIA_LEFT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val eyesColorIndex = (userData.niloColors[EYES_KEY] ?: 7).coerceIn(0, eyesOptions.size - 1)
    val whiskersColorIndex =
        (userData.niloColors[WHISKERS_KEY] ?: 7).coerceIn(0, hatBarOptions.size - 1)
    val macchieTypeIndex = (userData.niloColors[MACCHIE_TYPE_KEY] ?: 0).coerceIn(0, 3)

    // index 0 = base "macchie", 1 = tigrato, 2 = tuxedo, 3 = tuxedo-tigrato (tuxedo layer + the
    // tigrato left/right layers drawn together — there's no separate combo asset).
    val macchiePairs: List<Pair<DrawableResource, Color>> = when (macchieTypeIndex) {
        1 -> listOf(
            Res.drawable.nilo_layer_5_macchia_right_tigrato to hairOptions[macchiaRightColorIndex],
            Res.drawable.nilo_layer_5_macchia_left_tigrato to hairOptions[macchiaLeftColorIndex],
        )

        2 -> listOf(
            Res.drawable.nilo_layer_5_macchia_tuxedo to hairOptions[macchiaLeftColorIndex],
        )

        3 -> listOf(
            Res.drawable.nilo_layer_5_macchia_tuxedo to hairOptions[macchiaLeftColorIndex],
            Res.drawable.nilo_layer_5_macchia_right_tigrato to hairOptions[macchiaRightColorIndex],
            Res.drawable.nilo_layer_5_macchia_left_tigrato to hairOptions[macchiaLeftColorIndex],
        )

        else -> listOf(
            Res.drawable.nilo_layer_5_macchia_right to hairOptions[macchiaRightColorIndex],
            Res.drawable.nilo_layer_5_macchia_left to hairOptions[macchiaLeftColorIndex],
        )
    }

    val pairs: List<Pair<DrawableResource, Color>> = listOf(
        Res.drawable.nilo_layer_0_body to hairOptions[bodyColorIndex],
        Res.drawable.nilo_layer_1_ear_back_left to hairOptions[earLeftColorIndex],
        Res.drawable.nilo_layer_1_ear_back_right to hairOptions[earRightColorIndex],
        Res.drawable.nilo_layer_2_ear_mid to neutralWhite.reddish(),
        Res.drawable.nilo_layer_3_ear_top to neutralWhite.reddish(0.6f),
        Res.drawable.nilo_layer_4_head to hairOptions[headColorIndex],
    ) + macchiePairs

    val expression = when (face) {
        Faces.Sad -> listOf(
            Res.drawable.nilo_layer_6_idle_naso to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_idle_guance to neutralWhite.reddish(0.6f),
            Res.drawable.nilo_layer_6_sad_bocca_back to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_sad_bocca_over to neutralWhite.reddish(0.5f),
            Res.drawable.nilo_layer_6_idle_occhi_back to neutralWhite,
            Res.drawable.nilo_layer_6_idle_occhi_mid to eyesOptions[eyesColorIndex],
            Res.drawable.nilo_layer_6_idle_occhi_top to neutralWhite,
            Res.drawable.nilo_layer_6_idle_baffi to hatBarOptions[whiskersColorIndex],
        )

        Faces.Sleeping -> listOf(
            Res.drawable.nilo_layer_6_idle_naso to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_idle_guance to neutralWhite.reddish(0.6f),
            Res.drawable.nilo_layer_6_sleeping_bocca to neutralWhite.reddish(0.5f),
            Res.drawable.nilo_layer_6_sleeping_occhi to eyesOptions[eyesColorIndex].darken(0.3f),
            Res.drawable.nilo_layer_6_idle_baffi to hatBarOptions[whiskersColorIndex],
        )

        Faces.Surprised -> listOf(
            Res.drawable.nilo_layer_6_idle_naso to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_idle_guance to neutralWhite.reddish(0.6f),
            Res.drawable.nilo_layer_6_surprised_bocca_back to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_surprised_bocca_over to neutralWhite.reddish(0.5f),
            Res.drawable.nilo_layer_6_idle_occhi_back to neutralWhite,
            Res.drawable.nilo_layer_6_idle_occhi_mid to eyesOptions[eyesColorIndex],
            Res.drawable.nilo_layer_6_idle_occhi_top to neutralWhite,
            Res.drawable.nilo_layer_6_idle_baffi to hatBarOptions[whiskersColorIndex],
        )

        else -> listOf(
            Res.drawable.nilo_layer_6_idle_naso to neutralWhite.reddish(),
            Res.drawable.nilo_layer_6_idle_guance to neutralWhite.reddish(0.6f),
            Res.drawable.nilo_layer_6_idle_bocca_back to neutralWhite.reddish(),
            // Tuxedo/tuxedo-tigrato have a distinct spot-colored chin patch around the mouth,
            // not the plain base fur — everything else keeps the head color.
            Res.drawable.nilo_layer_6_idle_bocca_blockers to if (macchieTypeIndex == 2 || macchieTypeIndex == 3) {
                hairOptions[macchiaLeftColorIndex]
            } else {
                hairOptions[headColorIndex]
            },
            Res.drawable.nilo_layer_6_idle_bocca_over to neutralWhite.reddish(0.5f),
            Res.drawable.nilo_layer_6_idle_occhi_back to neutralWhite,
            Res.drawable.nilo_layer_6_idle_occhi_mid to eyesOptions[eyesColorIndex],
            Res.drawable.nilo_layer_6_idle_occhi_top to neutralWhite,
            Res.drawable.nilo_layer_6_idle_baffi to hatBarOptions[whiskersColorIndex],
        )
    }

    BuildAvatar(withBackground, bgColorIndex, pairs, expression, modifier, face)
}
